import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import SectionRosterModal from "./SectionRosterModal";
import s from "./SectionsByGradePanel.module.css";

function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "Content-Type": "application/json",
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId != null ? { "X-Linked-Entity-Id": String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType     ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

/**
 * Grade-level-grouped view of sections for a selected school year.
 * Shows:
 *   - faculty headers (Elementary / JHS / SHS), then
 *   - a card per grade level (dashed when empty),
 *   - a modal on click listing each section with adviser, student count,
 *     class-offering count, and edit/delete.
 *   - an inline quick-add form that opens inside the same modal, so admins
 *     don't have to leave the grade context to add another section.
 *
 * Props:
 *   sections        Array<{id, name, gradeLevelId, schoolYearId, adviserStaffId}>
 *   gradeLevels     Array<{id, name, departmentId, ordering}>
 *   schoolYears     Array<{id, label, active}>
 *   employees       Array<{id, name}>  — teacher/adviser candidates
 *   onEdit          fn(sectionRow)
 *   onDelete        fn(sectionRow)
 *   onAfterCreate   fn()               — parent refreshes section list after a create
 */
export default function SectionsByGradePanel({
  sections, gradeLevels, schoolYears, employees,
  onEdit, onDelete, onAfterCreate,
}) {
  const navigate = useNavigate();
  // Panel-owned fetches: data the generic AcademicsPage tab loader doesn't already have.
  // `faculties` here is shorthand for the TEACHING-tagged departments that
  // serve as the "Level" headers (Pre-K / Grade School / JHS / SHS) — the
  // Faculty entity was retired in the Faculty→Department merge.
  const [faculties,   setFaculties]   = useState([]);
  const [students,    setStudents]    = useState([]);
  const [offerings,   setOfferings]   = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  // Enrollments power the SectionRosterModal opened from card clicks.
  const [enrollments, setEnrollments] = useState([]);
  // Rooms power the homeroom resolution inside the StudentDetailModal
  // opened when a student card in the SectionRosterModal is clicked.
  const [rooms,       setRooms]       = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${API_BASE}/api/departments?staffType=TEACHING`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
    ]).then(([fac, stu, off, sem, enr, rms]) => {
      if (cancelled) return;
      setFaculties(fac || []);
      setStudents(stu || []);
      setOfferings(off || []);
      setSemesters(sem || []);
      setEnrollments(enr || []);
      setRooms(rms || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [syId, setSyId] = useState("");
  useEffect(() => {
    if (syId) return;
    const active = schoolYears.find(y => y.active) || schoolYears[0];
    if (active) setSyId(String(active.id));
  }, [schoolYears, syId]);

  const [openGradeId, setOpenGradeId] = useState(null);

  // Roster modal — opened by clicking a section card or "View roster"
  // affordance. The full section context (grade/faculty/adviser) is
  // captured at click time so the modal header stays correct even if a
  // refetch later mutates the shared `sections` array.
  const [rosterTarget, setRosterTarget] = useState(null);

  // Active semester for the currently-selected SY. Used to scope the
  // roster filter inside the modal so counts match what the admin
  // expects (this term, not historical).
  const activeSem = useMemo(() => {
    if (!syId) return null;
    const inSY = (semesters || []).filter(sm => String(sm.schoolYearId) === String(syId));
    return inSY.find(sm => sm.active) || inSY[0] || null;
  }, [syId, semesters]);
  const activeSyObj = useMemo(
    () => (schoolYears || []).find(y => String(y.id) === String(syId)) || null,
    [syId, schoolYears]
  );

  /** Build the rosterTarget payload for a section so the modal has full
   *  context: grade, faculty, adviser, and the active term. */
  function openRosterFor(sec) {
    const grade   = sec.gradeLevelId ? (gradeLevels || []).find(g => g.id === sec.gradeLevelId) : null;
    const faculty = grade ? (faculties || []).find(f => f.id === grade.departmentId) : null;
    const adviser = sec.adviserStaffId ? (employees || []).find(e => e.id === sec.adviserStaffId) : null;
    setRosterTarget({
      section:        { id: sec.id, name: sec.name },
      gradeLabel:     grade?.name || null,
      facultyLabel:   faculty?.name || null,
      adviserName:    adviser?.name || null,
    });
  }

  /** Deep-link to Directory > Enrollments tab pre-filtered to this
   *  section + the active term. Mirrors the handoff used by Class
   *  Offerings → SectionRoster. */
  function gotoDirectoryFor(sectionId) {
    try {
      sessionStorage.setItem("directory.deeplink", JSON.stringify({
        tab: "enrollments",
        sectionId,
        schoolYearId: activeSyObj?.id ?? null,
        semesterId:   activeSem?.id ?? null,
      }));
    } catch { /* sessionStorage unavailable */ }
    setRosterTarget(null);
    navigate("/directory");
  }

  // Three views:
  //   "cards"    — grade-level cards (default; click → see sections modal)
  //   "sections" — one card per section, flat alphabetical grid by grade
  //   "table"    — flat searchable table
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "sections" | "table"
  const [search, setSearch] = useState("");

  // Standalone "new section" modal used only in table view (grade is picked
  // inside the form since there's no grade-card context to inherit from).
  const [newOpen, setNewOpen] = useState(false);
  const [newGradeId, setNewGradeId] = useState("");

  // Inline quick-add state (form shown inside the modal when user clicks
  // "+ Add section to <grade>"). Scoped to the currently-open modal.
  const [addOpen,   setAddOpen]   = useState(false);
  const [addName,   setAddName]   = useState("");
  const [addAdviserId, setAddAdviserId] = useState("");
  const [addBusy,   setAddBusy]   = useState(false);
  const [addError,  setAddError]  = useState("");

  // Reset add state when modal or grade changes.
  useEffect(() => {
    setAddOpen(false);
    setAddName("");
    setAddAdviserId("");
    setAddError("");
    setAddBusy(false);
  }, [openGradeId]);

  // Escape closes the grade's sections modal (outside-click no longer does).
  useEffect(() => {
    if (openGradeId == null) return;
    function onKey(e) { if (e.key === "Escape") setOpenGradeId(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openGradeId]);

  // Escape closes the standalone "new section" modal (outside-click no
  // longer does).
  useEffect(() => {
    if (!newOpen) return;
    function onKey(e) { if (e.key === "Escape") setNewOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [newOpen]);

  async function saveNewSection(gradeLevelId) {
    if (!addName.trim()) {
      setAddError("Section name is required.");
      return;
    }
    if (!syId) {
      setAddError("Pick a school year first.");
      return;
    }
    setAddBusy(true); setAddError("");
    try {
      const payload = {
        name: addName.trim(),
        gradeLevelId: Number(gradeLevelId),
        schoolYearId: Number(syId),
        adviserStaffId: addAdviserId ? Number(addAdviserId) : null,
      };
      const res = await fetch(`${API_BASE}/api/sections`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setAddOpen(false);
      setAddName("");
      setAddAdviserId("");
      onAfterCreate && onAfterCreate();
      return true;
    } catch (e) {
      setAddError(e.message);
      return false;
    } finally {
      setAddBusy(false);
    }
  }

  async function saveNewFromTable() {
    if (!newGradeId) { setAddError("Pick a grade level."); return; }
    const ok = await saveNewSection(Number(newGradeId));
    if (ok) {
      setNewOpen(false);
      setNewGradeId("");
    }
  }

  // ── Derived lookups ─────────────────────────────────────────────────
  const facultyById    = useMemo(() => Object.fromEntries((faculties || []).map(f => [f.id, f])), [faculties]);
  const employeeById   = useMemo(() => Object.fromEntries((employees || []).map(e => [e.id, e])), [employees]);

  // Students per section (by currentSectionId).
  const studentsBySection = useMemo(() => {
    const map = new Map();
    for (const st of students) {
      if (!st.currentSectionId) continue;
      if ((st.status || "ENROLLED") !== "ENROLLED") continue;
      map.set(st.currentSectionId, (map.get(st.currentSectionId) || 0) + 1);
    }
    return map;
  }, [students]);

  // Class offerings per section, filtered to the selected SY (via semester.schoolYearId).
  const offeringsBySection = useMemo(() => {
    const semesterSyId = new Map(semesters.map(sm => [sm.id, String(sm.schoolYearId)]));
    const map = new Map();
    for (const o of offerings) {
      if (syId && semesterSyId.get(o.semesterId) !== String(syId)) continue;
      map.set(o.sectionId, (map.get(o.sectionId) || 0) + 1);
    }
    return map;
  }, [offerings, semesters, syId]);

  // Sections in the selected SY.
  const sectionsInSY = useMemo(
    () => (sections || []).filter(sec => !syId || String(sec.schoolYearId) === String(syId)),
    [sections, syId]
  );

  // Group sections by gradeLevelId.
  const sectionsByGrade = useMemo(() => {
    const map = new Map();
    for (const sec of sectionsInSY) {
      const key = sec.gradeLevelId ?? "ungraded";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(sec);
    }
    return map;
  }, [sectionsInSY]);

  // Ordered grade levels, grouped by faculty. Empty grades still shown (dashed).
  const gradesByFaculty = useMemo(() => {
    const ordered = [...(gradeLevels || [])].sort((a, b) => {
      const oa = a.ordering ?? 999;
      const ob = b.ordering ?? 999;
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "");
    });
    const map = new Map();
    for (const g of ordered) {
      const key = g.departmentId ?? "unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    }
    // Preserve faculty order by its first-appearing grade.
    return Array.from(map.entries()).map(([facultyId, grades]) => ({
      facultyId,
      faculty: facultyId === "unassigned" ? null : facultyById[facultyId],
      grades,
    }));
  }, [gradeLevels, facultyById]);

  // Any ungraded sections? Show a dangling card at the very end.
  const ungradedSections = sectionsByGrade.get("ungraded") || [];

  const openGrade = openGradeId != null
    ? (gradeLevels.find(g => g.id === openGradeId) || (openGradeId === "ungraded" ? { id: "ungraded", name: "Ungraded" } : null))
    : null;
  const openSections = openGrade
    ? (sectionsByGrade.get(openGrade.id) || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    : [];
  const openFacultyName = openGrade && openGrade.departmentId != null
    ? facultyById[openGrade.departmentId]?.name
    : null;

  const activeSyLabel = (schoolYears.find(y => String(y.id) === syId))?.label || "—";

  // Flat, searchable list used by the "All sections" table view.
  const gradesById = useMemo(
    () => Object.fromEntries((gradeLevels || []).map(g => [g.id, g])),
    [gradeLevels]
  );
  const flatSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sectionsInSY
      .map(sec => {
        const grade = gradesById[sec.gradeLevelId];
        const adviser = sec.adviserStaffId ? employeeById[sec.adviserStaffId] : null;
        return {
          sec, grade, adviser,
          gradeLabel:    grade?.name || "—",
          adviserName:   adviser?.name || "",
          studentCount:  studentsBySection.get(sec.id) || 0,
          offeringCount: offeringsBySection.get(sec.id) || 0,
        };
      })
      .filter(row => {
        if (!q) return true;
        return (
          row.sec.name.toLowerCase().includes(q) ||
          row.gradeLabel.toLowerCase().includes(q) ||
          row.adviserName.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const oa = a.grade?.ordering ?? 9999;
        const ob = b.grade?.ordering ?? 9999;
        if (oa !== ob) return oa - ob;
        return a.sec.name.localeCompare(b.sec.name);
      });
  }, [sectionsInSY, search, gradesById, employeeById, studentsBySection, offeringsBySection]);

  return (
    <>
      {/* SY filter + view toggle */}
      <div className={s.filterBar}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <div className={s.viewToggle} role="tablist" aria-label="View">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "cards"}
            className={`${s.toggleBtn} ${viewMode === "cards" ? s.toggleBtnActive : ""}`}
            onClick={() => setViewMode("cards")}>
            By Grade Level
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "sections"}
            className={`${s.toggleBtn} ${viewMode === "sections" ? s.toggleBtnActive : ""}`}
            onClick={() => setViewMode("sections")}>
            By Section
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "table"}
            className={`${s.toggleBtn} ${viewMode === "table" ? s.toggleBtnActive : ""}`}
            onClick={() => setViewMode("table")}>
            All Sections (List Form)
          </button>
        </div>
        <div className={s.summary}>
          <span className={s.pill}>{sectionsInSY.length} section{sectionsInSY.length === 1 ? "" : "s"}</span>
          <span className={s.pillMuted}>SY {activeSyLabel}</span>
        </div>
      </div>

      {viewMode === "table" && (
        <div className={s.tableView}>
          <div className={s.tableToolbar}>
            <input
              className={s.searchInput}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, grade, or adviser…"
            />
            <button
              type="button"
              className={s.btnPrimary}
              onClick={() => {
                setAddError("");
                setAddName("");
                setAddAdviserId("");
                setNewGradeId("");
                setNewOpen(true);
              }}>
              + New section
            </button>
          </div>

          {flatSections.length === 0 ? (
            <div className={s.empty}>
              {search ? "No sections match your search." : "No sections in this school year yet."}
            </div>
          ) : (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Grade</th>
                    <th>Adviser</th>
                    <th className={s.numCol}>Students</th>
                    <th className={s.numCol}>Offerings</th>
                    <th className={s.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flatSections.map(({ sec, gradeLabel, adviserName, studentCount, offeringCount }) => (
                    <tr key={sec.id}>
                      <td><b>{sec.name}</b></td>
                      <td>{gradeLabel}</td>
                      <td>{adviserName || <span className={s.warnInline}>none</span>}</td>
                      <td className={`${s.numCol} ${studentCount === 0 ? s.warnInline : ""}`}>{studentCount}</td>
                      <td className={`${s.numCol} ${offeringCount === 0 ? s.warnInline : ""}`}>{offeringCount}</td>
                      <td className={s.actionsCol}>
                        <button type="button" className={s.btnEdit} onClick={() => onEdit(sec)}>Edit</button>
                        <button type="button" className={s.btnDelete} onClick={() => onDelete(sec)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Flat section-card grid ─────────────────────────────────── */}
      {viewMode === "sections" && (
        sectionsInSY.length === 0 ? (
          <div className={s.empty}>No sections in this school year yet.</div>
        ) : (
          <div className={s.grid}>
            {sectionsInSY
              .slice()
              .sort((a, b) => {
                // Grade ordering first so same-grade sections stay together;
                // then section name so each grade's sections are alphabetical.
                const oa = gradesById[a.gradeLevelId]?.ordering ?? 9999;
                const ob = gradesById[b.gradeLevelId]?.ordering ?? 9999;
                if (oa !== ob) return oa - ob;
                return (a.name || "").localeCompare(b.name || "");
              })
              .map(sec => {
                const grade = gradesById[sec.gradeLevelId];
                const faculty = grade ? facultyById[grade.departmentId] : null;
                const adviser = sec.adviserStaffId ? employeeById[sec.adviserStaffId] : null;
                const studentCount  = studentsBySection.get(sec.id)  || 0;
                const offeringCount = offeringsBySection.get(sec.id) || 0;
                return (
                  <div
                    key={sec.id}
                    className={`${s.sectionCard} ${s.sectionCardClickable}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open roster for ${sec.name}`}
                    onClick={() => openRosterFor(sec)}
                    onKeyDown={(e) => {
                      // Only handle keystrokes that originated on the
                      // card itself — Enter/Space on a focused inner
                      // button (Edit/Delete) bubbles here too, and we
                      // don't want it to also fire the roster click.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openRosterFor(sec);
                      }
                    }}>
                    <div className={s.sectionCardTop}>
                      <div className={s.sectionCardName}>{sec.name}</div>
                      <div className={s.sectionCardMeta}>
                        {grade?.name || "—"}
                        {faculty ? ` · ${faculty.name}` : ""}
                      </div>
                    </div>
                    <div className={s.sectionCardAdviser}>
                      Adviser: {adviser?.name || <em className={s.warnInline}>none</em>}
                    </div>
                    <div className={s.sectionCardPills}>
                      <span className={`${s.statPill} ${studentCount === 0 ? s.statPillWarn : ""}`}>
                        {studentCount} student{studentCount === 1 ? "" : "s"}
                      </span>
                      <span className={`${s.statPill} ${offeringCount === 0 ? s.statPillWarn : ""}`}>
                        {offeringCount} offering{offeringCount === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className={s.sectionCardActions}>
                      <button
                        type="button"
                        className={s.btnEdit}
                        onClick={(e) => { e.stopPropagation(); onEdit(sec); }}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={s.btnDelete}
                        onClick={(e) => { e.stopPropagation(); onDelete(sec); }}>
                        Delete
                      </button>
                    </div>
                    <span className={s.sectionCardHint} aria-hidden="true">View roster →</span>
                  </div>
                );
              })}
          </div>
        )
      )}

      {/* Faculty groups */}
      {viewMode === "cards" && gradesByFaculty.map(({ facultyId, faculty, grades }) => (
        <div key={facultyId} className={s.facultyGroup}>
          <div className={s.facultyHead}>{faculty?.name || "Unassigned faculty"}</div>
          <div className={s.grid}>
            {grades.map(g => {
              const list = sectionsByGrade.get(g.id) || [];
              const preview = list.slice(0, 3).map(x => x.name).join(" · ") || "—";
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`${s.gradeCard} ${list.length === 0 ? s.gradeCardEmpty : ""}`}
                  onClick={() => setOpenGradeId(g.id)}>
                  <div className={s.gradeName}>{g.name}</div>
                  <div className={s.gradeCount}>
                    {list.length} section{list.length === 1 ? "" : "s"}
                  </div>
                  <div className={s.gradePreview}>{preview}</div>
                  <div className={s.gradeHint}>View →</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {viewMode === "cards" && ungradedSections.length > 0 && (
        <div className={s.facultyGroup}>
          <div className={s.facultyHead}>Sections without a grade level</div>
          <div className={s.grid}>
            <button
              type="button"
              className={`${s.gradeCard} ${s.gradeCardWarn}`}
              onClick={() => setOpenGradeId("ungraded")}>
              <div className={s.gradeName}>Ungraded</div>
              <div className={s.gradeCount}>{ungradedSections.length} section{ungradedSections.length === 1 ? "" : "s"}</div>
              <div className={s.gradeHint}>View →</div>
            </button>
          </div>
        </div>
      )}

      {openGrade && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <h3 className={s.modalTitle}>{openGrade.name}</h3>
                <p className={s.modalSub}>
                  {openFacultyName ? `${openFacultyName} · ` : ""}
                  SY {activeSyLabel} · {openSections.length} section{openSections.length === 1 ? "" : "s"}
                </p>
              </div>
              <button type="button" className={s.closeBtn} onClick={() => setOpenGradeId(null)}>Close</button>
            </div>

            <div className={s.modalBody}>
              {addOpen && openGrade.id !== "ungraded" ? (
                <form
                  className={s.addForm}
                  onSubmit={e => { e.preventDefault(); saveNewSection(openGrade.id); }}>
                  <div className={s.addContext}>
                    Adding to <b>{openGrade.name}</b> · SY {activeSyLabel}
                  </div>
                  {addError && <div className={s.addError}>{addError}</div>}
                  <div className={s.addField}>
                    <label className={s.addLabel}>Name <span className={s.addReq}>*</span></label>
                    <input
                      className={s.addInput}
                      autoFocus
                      value={addName}
                      onChange={e => setAddName(e.target.value)}
                      placeholder="St. Therese"
                      required
                    />
                  </div>
                  <div className={s.addField}>
                    <label className={s.addLabel}>Adviser</label>
                    <select
                      className={s.addInput}
                      value={addAdviserId}
                      onChange={e => setAddAdviserId(e.target.value)}>
                      <option value="">— none —</option>
                      {(employees || []).map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                      ))}
                    </select>
                  </div>
                </form>
              ) : openSections.length === 0 ? (
                <div className={s.empty}>No sections for {openGrade.name} in this school year yet.</div>
              ) : (
                <ul className={s.sectionList}>
                  {openSections.map(sec => {
                    const adviser = sec.adviserStaffId ? employeeById[sec.adviserStaffId] : null;
                    const studentCount = studentsBySection.get(sec.id) || 0;
                    const offeringCount = offeringsBySection.get(sec.id) || 0;
                    return (
                      <li key={sec.id} className={s.sectionRow}>
                        <div className={s.sectionMain}>
                          <div className={s.sectionName}>{sec.name}</div>
                          <div className={s.sectionMeta}>
                            <span>Adviser: {adviser?.name || <em className={s.warnInline}>none</em>}</span>
                            <span className={studentCount === 0 ? s.warnInline : ""}>
                              {studentCount} student{studentCount === 1 ? "" : "s"}
                            </span>
                            <span className={offeringCount === 0 ? s.warnInline : ""}>
                              {offeringCount} class offering{offeringCount === 1 ? "" : "s"}
                            </span>
                          </div>
                        </div>
                        <div className={s.actions}>
                          <button
                            type="button"
                            className={s.btnRoster}
                            onClick={() => { setOpenGradeId(null); openRosterFor(sec); }}>
                            View roster
                          </button>
                          <button
                            type="button"
                            className={s.btnEdit}
                            onClick={() => { setOpenGradeId(null); onEdit(sec); }}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className={s.btnDelete}
                            onClick={() => onDelete(sec)}>
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className={s.modalFoot}>
              {addOpen ? (
                <>
                  <button type="button" className={s.btnGhost} onClick={() => setAddOpen(false)} disabled={addBusy}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={s.btnPrimary}
                    disabled={addBusy}
                    onClick={() => saveNewSection(openGrade.id)}>
                    {addBusy ? "Saving…" : "Create section"}
                  </button>
                </>
              ) : openGrade.id !== "ungraded" && (
                <button
                  type="button"
                  className={s.btnPrimary}
                  onClick={() => setAddOpen(true)}>
                  + Add section to {openGrade.name}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {newOpen && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <h3 className={s.modalTitle}>New section</h3>
                <p className={s.modalSub}>SY {activeSyLabel}</p>
              </div>
              <button type="button" className={s.closeBtn} onClick={() => setNewOpen(false)}>Close</button>
            </div>

            <div className={s.modalBody}>
              <form className={s.addForm} onSubmit={e => { e.preventDefault(); saveNewFromTable(); }}>
                {addError && <div className={s.addError}>{addError}</div>}
                <div className={s.addField}>
                  <label className={s.addLabel}>Grade level <span className={s.addReq}>*</span></label>
                  <select
                    className={s.addInput}
                    value={newGradeId}
                    onChange={e => setNewGradeId(e.target.value)}
                    required>
                    <option value="">— select —</option>
                    {(gradeLevels || []).slice().sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)).map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{facultyById[g.departmentId]?.name ? ` · ${facultyById[g.departmentId].name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={s.addField}>
                  <label className={s.addLabel}>Name <span className={s.addReq}>*</span></label>
                  <input
                    className={s.addInput}
                    autoFocus
                    value={addName}
                    onChange={e => setAddName(e.target.value)}
                    placeholder="St. Therese"
                    required
                  />
                </div>
                <div className={s.addField}>
                  <label className={s.addLabel}>Adviser</label>
                  <select
                    className={s.addInput}
                    value={addAdviserId}
                    onChange={e => setAddAdviserId(e.target.value)}>
                    <option value="">— none —</option>
                    {(employees || []).map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
              </form>
            </div>

            <div className={s.modalFoot}>
              <button type="button" className={s.btnGhost} onClick={() => setNewOpen(false)} disabled={addBusy}>
                Cancel
              </button>
              <button
                type="button"
                className={s.btnPrimary}
                disabled={addBusy}
                onClick={saveNewFromTable}>
                {addBusy ? "Saving…" : "Create section"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <SectionRosterModal
        open={!!rosterTarget}
        section={rosterTarget?.section || null}
        gradeLabel={rosterTarget?.gradeLabel}
        facultyLabel={rosterTarget?.facultyLabel}
        adviserName={rosterTarget?.adviserName}
        schoolYearId={activeSyObj?.id ?? null}
        semesterId={activeSem?.id ?? null}
        schoolYearLabel={activeSyObj?.label || null}
        semesterLabel={activeSem?.label || null}
        students={students}
        enrollments={enrollments}
        sections={sections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        rooms={rooms}
        onClose={() => setRosterTarget(null)}
        onManage={() => rosterTarget && gotoDirectoryFor(rosterTarget.section.id)}
      />
    </>
  );
}
